import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { ManageHeader, SearchBar, Fab, EmptyState, Field, TextInput, TextArea, SelectInput, Toggle } from '../../components/mobile/ManageKit';
import { Users, MapPin, Archive, ChevronRight, Loader2 } from 'lucide-react';

const empty = {
  customer_type: 'organisation', company_name: '', contact_name: '', phone: '', email: '',
  address: '', abn: '', notes: '', requires_po: false,
  payment_terms: '', credit_limit: '', docket_to_email: '', price_list_id: '',
};

const MobileManageCustomers = () => {
  const { api, can } = useMobileAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmArch, setConfirmArch] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [custRes, plRes] = await Promise.all([
        api.get('/api/customers', { params: { status: 'active' } }),
        api.get('/api/price-lists').catch(() => ({ data: [] })),
      ]);
      setItems(custRes.data || []);
      setPriceLists(plRes.data || []);
    } catch (e) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setConfirmArch(false); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...Object.fromEntries(Object.entries(c).filter(([, v]) => v != null)) }); setConfirmArch(false); setOpen(true); };

  const save = async () => {
    if (form.customer_type === 'organisation' && !(form.company_name || '').trim()) return toast.error('Company name is required');
    if (form.customer_type === 'individual' && !(form.company_name || '').trim() && !(form.contact_name || '').trim()) return toast.error('Full name is required');
    setSaving(true);
    try {
      const payload = {
        customer_type: form.customer_type,
        company_name: form.company_name || null,
        contact_name: form.contact_name || null,
        phone: form.phone || null,
        email: form.email || null,
        address: form.address || null,
        abn: form.abn || null,
        notes: form.notes || null,
        requires_po: form.requires_po,
        payment_terms: form.payment_terms || null,
        credit_limit: form.credit_limit ? Number(form.credit_limit) : null,
        docket_to_email: form.docket_to_email || null,
        price_list_id: form.price_list_id || null,
      };
      if (editing) await api.put(`/api/customers/${editing.id}`, payload);
      else await api.post('/api/customers', payload);
      toast.success(editing ? 'Customer updated' : 'Customer added');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    try {
      await api.delete(`/api/customers/${editing.id}`);
      toast.success('Customer archived');
      setOpen(false);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.blocked) {
        const r = detail.reasons || {};
        toast.error(`Cannot archive: ${r.open_jobs || 0} open jobs, ${r.open_dockets || 0} dockets`);
      } else {
        toast.error(detail || 'Cannot archive');
      }
    }
  };

  const filtered = items.filter((c) =>
    `${c.company_name || ''} ${c.contact_name || ''}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <MobileLayout>
      <ManageHeader title="Customers" subtitle={`${items.length} active`} />
      <div className="p-4 space-y-3">
        <SearchBar value={q} onChange={setQ} placeholder="Search customers..." />
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 text-[#ffd108] animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No customers" subtitle="Tap Add to create your first customer" />
        ) : (
          <div className="space-y-2" data-testid="customers-list">
            {filtered.map((c) => (
              <div key={c.id} className="bg-[var(--m-bg-card)] border border-[var(--m-border)] rounded-lg overflow-hidden" data-testid={`customer-row-${c.id}`}>
                <button onClick={() => openEdit(c)} className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-[var(--m-bg-elevated)]">
                  <div className="w-9 h-9 rounded-lg bg-[var(--m-bg)] flex items-center justify-center shrink-0">
                    <Users className="h-5 w-5 text-[#ffd108]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--m-text)] font-medium truncate">{c.company_name || c.contact_name}</div>
                    <div className="text-xs text-[var(--m-text-secondary)] truncate">{c.phone || c.email || 'No contact info'}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[var(--m-text-muted)]" />
                </button>
                <button onClick={() => navigate(`/mobile/manage/customers/${c.id}/sites`, { state: { customerName: c.company_name || c.contact_name } })}
                  className="w-full flex items-center gap-2 px-4 py-2 border-t border-[var(--m-border)]/60 text-xs text-[var(--m-text-body)] active:bg-[var(--m-bg-elevated)]"
                  data-testid={`customer-sites-btn-${c.id}`}>
                  <MapPin className="h-3.5 w-3.5 text-[#ffd108]" /> Manage sites{typeof c.site_count === 'number' ? ` (${c.site_count})` : ''}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <Fab onClick={openNew} label="Add" testId="customer-add-fab" visible={can('customers.write')} />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="bg-[var(--m-bg)] border-[var(--m-border)] rounded-t-2xl max-h-[90vh] overflow-y-auto">
          <SheetHeader><SheetTitle className="text-[var(--m-text)] text-left">{editing ? 'Edit Customer' : 'New Customer'}</SheetTitle></SheetHeader>
          <div className="py-4 space-y-3">
            <Field label="Type">
              <SelectInput testId="cust-type" value={form.customer_type} onChange={(e) => setForm({ ...form, customer_type: e.target.value })} placeholder=""
                options={[{ value: 'organisation', label: 'Organisation' }, { value: 'individual', label: 'Individual' }]} />
            </Field>
            <Field label={form.customer_type === 'individual' ? 'Full name' : 'Company name'} required>
              <TextInput testId="cust-company" value={form.company_name || ''} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder={form.customer_type === 'individual' ? 'John Smith' : 'Acme Constructions'} />
            </Field>
            {form.customer_type === 'organisation' && (
              <Field label="Primary contact"><TextInput testId="cust-contact" value={form.contact_name || ''} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></Field>
            )}
            <Field label="Phone"><TextInput testId="cust-phone" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0400 000 000" /></Field>
            <Field label="Email"><TextInput testId="cust-email" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Address"><TextInput testId="cust-address" value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="ABN"><TextInput testId="cust-abn" value={form.abn || ''} onChange={(e) => setForm({ ...form, abn: e.target.value })} /></Field>
            <Field label="Docket email (auto-send)"><TextInput testId="cust-docket-email" type="email" value={form.docket_to_email || ''} onChange={(e) => setForm({ ...form, docket_to_email: e.target.value })} placeholder="invoices@client.com" /></Field>
            <Field label="Payment terms">
              <SelectInput testId="cust-terms" value={form.payment_terms || ''} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                placeholder="Select" options={[{ value: '', label: 'None' }, { value: '7', label: '7 days' }, { value: '14', label: '14 days' }, { value: '30', label: '30 days' }, { value: '60', label: '60 days' }, { value: 'EOM', label: 'End of month' }]} />
            </Field>
            <Field label="Credit limit"><TextInput testId="cust-credit-limit" type="number" value={form.credit_limit || ''} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} placeholder="0 = no limit" /></Field>
            {priceLists.length > 0 && (
              <Field label="Price list">
                <SelectInput testId="cust-price-list" value={form.price_list_id || ''} onChange={(e) => setForm({ ...form, price_list_id: e.target.value })}
                  placeholder="Default" options={[{ value: '', label: 'Default pricing' }, ...priceLists.map((pl) => ({ value: pl.id, label: pl.name }))]} />
              </Field>
            )}
            <Field label="Notes"><TextArea testId="cust-notes" value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            <Toggle label="Requires PO number" checked={form.requires_po} onChange={(v) => setForm({ ...form, requires_po: v })} testId="cust-requires-po" />
          </div>
          <div className="space-y-2 pb-2">
            <Button className="btn-volt w-full" onClick={save} disabled={saving} data-testid="cust-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Save changes' : 'Add customer')}
            </Button>
            {editing && (
              confirmArch ? (
                <Button variant="destructive" className="w-full" onClick={archive} data-testid="cust-archive-confirm">Tap again to confirm archive</Button>
              ) : (
                <Button variant="outline" className="w-full border-amber-800 text-amber-400" onClick={() => setConfirmArch(true)} data-testid="cust-archive">
                  <Archive className="h-4 w-4 mr-2" /> Archive
                </Button>
              )
            )}
          </div>
        </SheetContent>
      </Sheet>
    </MobileLayout>
  );
};

export default MobileManageCustomers;
